import { Button } from "@itandi/itandi-bb-ui/Button";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import React from "react";
import { useForm } from "react-hook-form";
import { useSessions } from "~/hooks/vendors/useSessions";
import type { Deal } from "~/models/Deal";
import { getSelfDealVendor } from "~/models/DealVendorCompany";
import type { VisitScheduleInputVendorParam } from "~/models/DealVisitDate";
import { isSuccess } from "~/utils/api/api_handler";
import { update } from "~/utils/api/vendors/DealVisitDate";
import { toDate } from "~/utils/DateUtil";
import { ScheduleForm } from "./ScheduleForm";

type Props = {
	deal: Deal;
	handleClose: () => void;
	handleMutate: () => Promise<void>;
	isOpen: boolean;
};

/**
 * 立会日程・現地調査日程登録モーダル
 *
 * @for vendor
 */

export function ScheduleInputModal({
	deal,
	isOpen,
	handleClose,
	handleMutate
}: Props) {
	const { notify } = useNotification();
	const session = useSessions().index().data!;
	const selfVendorUserId = React.useMemo(() => {
		const selfVendor = getSelfDealVendor(deal, session.vendorCompany.id);
		if (selfVendor === null) return null;
		return selfVendor.user?.id ?? null;
	}, [deal, session]);
	const methods = useForm<VisitScheduleInputVendorParam>({
		defaultValues: {
			inspectionDate: toDate(deal.inspectionDate),
			surveyDate: toDate(deal.surveyDate),
			vendorUserId: selfVendorUserId
		}
	});
	const { watch, formState, handleSubmit } = methods;
	const inspectionDate = watch("inspectionDate");
	const surveyDate = watch("surveyDate");
	const vendorUserId = watch("vendorUserId");
	const onClickSubmit = React.useCallback(() => {
		handleSubmit(async () => {
			const response = await update(
				deal.id,
				inspectionDate,
				surveyDate,
				vendorUserId
			);
			if (isSuccess(response)) {
				await handleMutate();
				notify({ content: "追加しました。", variant: "success" });
				handleClose();
			} else {
				notify({ content: "エラーが発生しました。", variant: "error" });
			}
		})();
	}, [
		deal,
		handleClose,
		handleMutate,
		handleSubmit,
		inspectionDate,
		notify,
		surveyDate,
		vendorUserId
	]);
	return (
		<Modalv2 isOpen={isOpen} onClose={handleClose}>
			<ModalHeader
				title={`${
					deal.inspectionBy === "vendor"
						? "立会日程・現地調査日程"
						: "現地調査日程"
				}登録`}
			/>
			<ModalBody>
				<ScheduleForm deal={deal} methods={methods} session={session} />
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button
						onClick={(): void => {
							handleClose();
						}}
						variant="secondary"
					>
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						disabled={formState.isSubmitting}
						onClick={onClickSubmit}
						variant="primary"
					>
						確定
					</Button>
				}
			/>
		</Modalv2>
	);
}
